import { BadgeCheck, User, Shield, FileText, Edit3, AlertCircle, CheckCircle2 } from "lucide-react";
import VerificationStatusItem from "./VerificationStatusItem";
import Button from "../../../components/common/Button";

const EnhancedProfileCompletionCard = ({ 
    profileCompletion, 
    verificationData, 
    onUpdateProfile, 
    onViewVerification 
}) => {
    const { 
        is_verified, 
        verification_percentage = 0,
        education_verified,
        employment_verified, 
        identity_verified,
        licenses_verified,
        pending_documents = []
    } = verificationData || {};


    const showCompletionMessage = profileCompletion < 100;
    const showVerificationProgress = verification_percentage > 0 && verification_percentage < 100;
    const showFullVerification = verification_percentage === 100;
    const hasPendingDocuments = pending_documents.length > 0;
    const needsAttention = hasPendingDocuments || profileCompletion < 70;


    if (showFullVerification) {
        return (
            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-5 shadow-lg relative overflow-hidden border border-green-400/20">
                {/* Simplified background elements */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-12 translate-x-12"></div>
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full translate-y-8 -translate-x-8"></div>
                
                {/* Success Badge - More subtle */}
                <div className="absolute top-3 right-3 flex items-center gap-1 bg-white/20 px-2 py-1 rounded-full text-xs font-medium">
                    <CheckCircle2 className="w-3 h-3" />
                    Verified
                </div>

                <div className="relative z-10">
                    <div className="flex items-start space-x-3 mb-4">
                        <div className="p-2 bg-white/20 rounded-lg">
                            <BadgeCheck className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-semibold text-white text-base">Profile Complete & Verified!</h3>
                            <p className="text-green-100 text-xs mt-1">Your profile stands out to employers</p>
                        </div>
                    </div>
                    
                    {/* Verification Status Grid - More compact */}
                    <div className="grid grid-cols-2 gap-2 mb-4">
                        <VerificationStatusItem label="Identity" verified={identity_verified} compact />
                        <VerificationStatusItem label="Licenses" verified={licenses_verified} compact />
                        <VerificationStatusItem label="Employment" verified={employment_verified} compact />
                        <VerificationStatusItem label="Education" verified={education_verified} compact />
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                        <Button 
                            onClick={onUpdateProfile}
                            className="bg-white !text-green-700 hover:!bg-green-50 flex-1 font-medium py-2 text-sm"
                            size="sm"
                        >
                            <Edit3 className="w-3 h-3 mr-1" />
                            Update
                        </Button>
                        <Button 
                            onClick={onViewVerification}
                            variant="outline"
                            className="border-white text-white hover:bg-white/20 flex-1 font-medium py-2 text-sm"
                            size="sm"
                        >
                            <Shield className="w-3 h-3 mr-1" />
                            Status
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`bg-gradient-to-br rounded-xl p-5 shadow-lg relative overflow-hidden border ${
            needsAttention 
                ? 'from-amber-500 to-amber-600 border-amber-400/20' 
                : 'from-blue-500 to-blue-600 border-blue-400/20'
        }`}>
            {/* Simplified background elements */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-12 translate-x-12"></div>
            <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full translate-y-8 -translate-x-8"></div>
            
            {/* Status Badge - More subtle */}
            {needsAttention && (
                <div className="absolute top-3 right-3 flex items-center gap-1 bg-red-500 px-2 py-1 rounded-full text-xs text-white font-medium">
                    <AlertCircle className="w-3 h-3" />
                    Action Needed
                </div>
            )}

            <div className="relative z-10">
                {/* Header - More compact */}
                <div className="flex items-start space-x-3 mb-4">
                    <div className={`p-2 rounded-lg ${
                        needsAttention ? 'bg-amber-500/30' : 'bg-white/20'
                    }`}>
                        <User className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-semibold text-white text-base">Complete Your Profile</h3>
                        <p className="text-white/80 text-xs mt-1">
                            {needsAttention 
                                ? 'Complete your profile to unlock better job matches' 
                                : 'Unlock better job matches and increase your visibility'
                            }
                        </p>
                    </div>
                </div>

                {/* Progress Sections - Better spacing */}
                <div className="space-y-3 mb-4">
                    {/* Profile Completion */}
                    {/* {showCompletionMessage && ( */}
                        <div className="space-y-1">
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-white/80">Profile Completion</span>
                                <span className="font-bold text-white">{profileCompletion}%</span>
                            </div>
                            <div className="w-full bg-white/20 rounded-full h-2">
                                <div 
                                    className={`h-2 rounded-full transition-all duration-700 ease-out ${
                                        profileCompletion < 50 ? 'bg-amber-300' : 
                                        profileCompletion < 80 ? 'bg-blue-300' : 'bg-green-300'
                                    }`}
                                    style={{ width: `${profileCompletion}%` }}
                                ></div>
                            </div>
                        </div>
                    {/* )} */}

                    {/* Verification Progress */}
                    {showVerificationProgress && (
                        <div className="space-y-1">
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-white/80">Verification Progress</span>
                                <span className="font-bold text-green-200">{verification_percentage}%</span>
                            </div>
                            <div className="w-full bg-white/20 rounded-full h-2">
                                <div 
                                    className="bg-green-300 h-2 rounded-full transition-all duration-700 ease-out"
                                    style={{ width: `${verification_percentage}%` }}
                                ></div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Action Buttons - Better hierarchy */}
                <div className="space-y-2">
                    <Button 
                        onClick={onUpdateProfile}
                        className={`w-full font-medium py-3 text-sm ${
                            needsAttention 
                                ? 'bg-white !text-amber-600 hover:!bg-amber-50' 
                                : 'bg-white !text-blue-600 hover:!bg-blue-50'
                        }`}
                        size="sm"
                    >
                        <Edit3 className="w-3 h-3 mr-1" />
                        {profileCompletion < 50 ? 'Complete Profile' : 'Update Profile'}
                    </Button>
                    
                    <Button 
                        onClick={onViewVerification}
                        className={`w-full font-medium py-3 text-sm text-white  ${
                            hasPendingDocuments 
                                ? needsAttention ? '!bg-amber-700 hover:!bg-amber-800' : '!bg-amber-500 hover:!bg-amber-600' 
                                : 'bg-white/20 text-white hover:bg-white/30 border border-white/30'
                        }`}
                        size="sm"
                    >
                        {hasPendingDocuments ? (
                            <>
                                <FileText className="w-3 h-3 mr-1" />
                                Complete Verification
                                {pending_documents.length > 0 && (
                                    <span className="ml-1 bg-white/20 px-1.5 py-0.5 rounded-full text-xs">
                                        {pending_documents.length}
                                    </span>
                                )}
                            </>
                        ) : (
                            <>
                                <Shield className="w-3 h-3 mr-1" />
                                View Verification
                            </>
                        )}
                    </Button>
                </div>

                {/* Status Messages - More subtle */}
                {hasPendingDocuments && (
                    <p className="text-white/70 text-xs text-center mt-2">
                        {pending_documents.length} document(s) pending verification
                    </p>
                )}

                {showVerificationProgress && !hasPendingDocuments && (
                    <div className="flex items-center justify-center gap-1 mt-2 text-white/70 text-xs">
                        <Shield className="w-2.5 h-2.5" />
                        <span>Verification in progress - {verification_percentage}% complete</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EnhancedProfileCompletionCard;